import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import DateField from 'modules/Orders/DateField';
import OrderAccess from 'modules/Orders/OrderAccess';
import * as OrderStyles from 'modules/Orders/Orders.css';
import PageHeader from 'modules/Orders/PageHeader';
import SearchableSelect from 'modules/Orders/SearchableSelect';
import { isInvalidDate } from 'modules/Orders/utils';
import { displayLabelRows } from 'utils/displayLabelRows';
import { fetchOrderReadiness } from './api';
import {
  bulkIssueUrl,
  filterProblems,
  groupForIssue,
  isIssuable,
  normalizeReadiness,
  orderStatusLabel,
  ProblemFilters,
  problemKey,
  readinessCsv,
} from './readiness';
import ReadinessSummary from './ReadinessSummary';
import { OrderReadiness, ReadinessProblem } from './types';
import * as Styles from './Readiness.css';

const PAGE_SIZE = 50;

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

const saveCsv = (content: string, filename: string) => {
  // The byte order mark lets spreadsheets read Polish names as UTF-8.
  const url = URL.createObjectURL(
    new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const INITIAL_FILTERS: ProblemFilters = {
  status: '',
  clientId: '',
  owner: '',
  reportingOnly: true,
  query: '',
};

// Go-live readiness: whether order validation can be enabled on a date, the
// checks behind that verdict and the consultant x client pairs to fix.
export const OrderReportingCompletenessContent = ({
  canManage = false,
}: {
  canManage?: boolean;
}) => {
  const intl = useIntl();
  const history = useHistory();
  // An empty date lets the backend choose the go-live date.
  const [date, setDate] = useState('');
  const [includeInternal, setIncludeInternal] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(0);
  const [data, setData] = useState<OrderReadiness>();
  const request = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++request.current;
    setLoading(true);
    setData(undefined);
    setSelected(new Set());
    fetchOrderReadiness(
      { date: date || undefined, includeInternal },
      controller.signal,
    )
      .then((result) => {
        if (controller.signal.aborted || request.current !== requestId) {
          return;
        }
        setStatus(result.status);
        setData(result.ok ? normalizeReadiness(result.data) : undefined);
      })
      .catch((error) => {
        if (
          (error as Error).name !== 'AbortError' &&
          request.current === requestId
        ) {
          setStatus(0);
          setData(undefined);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted && request.current === requestId) {
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [date, includeInternal]);

  const problems = useMemo(() => data?.problems || [], [data]);
  const rows = useMemo(
    () => filterProblems(problems, filters),
    [problems, filters],
  );
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const issuableKeys = rows.filter(isIssuable).map(problemKey);
  const pickedCount = issuableKeys.filter((key) => selected.has(key)).length;
  const issueGroups = useMemo(
    () => groupForIssue(problems, selected),
    [problems, selected],
  );

  const clientOptions = useMemo(
    () =>
      Array.from(
        new Map(
          problems.map(({ clientId, clientName }) => [clientId, clientName]),
        ),
      )
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [problems],
  );
  const ownerOptions = useMemo(
    () =>
      Array.from(
        new Set(
          problems
            .map(({ ownerName }) => ownerName)
            .filter((name): name is string => Boolean(name)),
        ),
      )
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [problems],
  );

  const updateFilters = (patch: Partial<ProblemFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(0);
  };

  const toggle = (keys: string[], checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      keys.forEach((key) => (checked ? next.add(key) : next.delete(key)));
      return next;
    });

  const exportCsv = () =>
    saveCsv(
      readinessCsv(rows, intl),
      `go-live-readiness-${data?.date || 'report'}.csv`,
    );

  const label = (id: string) => intl.formatMessage({ id });
  const hours = (value: number) =>
    intl.formatNumber(value, { maximumFractionDigits: 2 });

  const renderRow = (row: ReadinessProblem) => {
    const key = problemKey(row);
    const orderPath =
      row.orderNumber && `/assignments/${encodeURIComponent(row.orderNumber)}`;
    const openOrder = orderPath ? () => history.push(orderPath) : undefined;
    const RowComponent = openOrder ? OrderStyles.ClickableRow : TableRow;
    const deadlineAfterDate =
      row.orderStatus === 'PENDING' &&
      Boolean(row.decisionDeadline && data?.date) &&
      String(row.decisionDeadline) > String(data?.date);
    return (
      <RowComponent key={key} hover onClick={openOrder}>
        {canManage && (
          <TableCell
            padding="checkbox"
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              size="small"
              checked={selected.has(key)}
              disabled={!isIssuable(row)}
              onChange={(event) => toggle([key], event.target.checked)}
              inputProps={{
                'aria-label': intl.formatMessage(
                  { id: 'ORDER_REPORTING.SELECT_ROW' },
                  { name: row.consultantName, client: row.clientName },
                ),
              }}
            />
          </TableCell>
        )}
        <TableCell>
          {row.consultantName}
          {row.consultantUsername &&
            row.consultantUsername !== row.consultantName && (
              <OrderStyles.CellNote>
                {row.consultantUsername}
              </OrderStyles.CellNote>
            )}
        </TableCell>
        <TableCell>{row.clientName}</TableCell>
        <TableCell>
          <Styles.Pill $color={Styles.ORDER_STATUS_COLORS[row.orderStatus]}>
            {orderStatusLabel(intl, row.orderStatus)}
          </Styles.Pill>
        </TableCell>
        <OrderStyles.NoWrapCell>
          {orderPath ? (
            <Link to={orderPath} onClick={(event) => event.stopPropagation()}>
              {row.orderNumber}
            </Link>
          ) : (
            '—'
          )}
        </OrderStyles.NoWrapCell>
        <OrderStyles.NoWrapCell>
          {row.orderStatus === 'PENDING' ? (
            <Typography
              variant="body2"
              component="span"
              color={deadlineAfterDate ? 'error' : 'inherit'}
            >
              {formatDate(row.decisionDeadline)}
              {deadlineAfterDate && (
                <OrderStyles.CellNote>
                  {label('ORDER_REPORTING.AFTER_DATE')}
                </OrderStyles.CellNote>
              )}
            </Typography>
          ) : (
            '—'
          )}
        </OrderStyles.NoWrapCell>
        <TableCell>{row.ownerName || '—'}</TableCell>
        <TableCell align="right">{hours(row.reportedHours)}</TableCell>
        <TableCell align="right">
          {row.hoursWithoutOrder > 0 ? (
            <Typography variant="body2" component="span" color="error">
              {hours(row.hoursWithoutOrder)}
            </Typography>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell align="right">{row.needsCorrection || '—'}</TableCell>
        <OrderStyles.NoWrapCell>
          {formatDate(row.lastReportedDate)}
        </OrderStyles.NoWrapCell>
      </RowComponent>
    );
  };

  return (
    <OrderStyles.Page>
      <PageHeader
        title={label('ORDER_REPORTING.READINESS_TITLE')}
        description={label('ORDER_REPORTING.READINESS_DESCRIPTION')}
        status={
          data?.mode && (
            <>
              <Chip
                size="small"
                variant="outlined"
                label={label(`ORDER_REPORTING.MODE.${data.mode}`)}
              />
              {data.effectiveDate && (
                <Typography variant="body2" color="textSecondary">
                  {intl.formatMessage(
                    { id: 'ORDER_REPORTING.GO_LIVE' },
                    { date: formatDate(data.effectiveDate) },
                  )}
                </Typography>
              )}
            </>
          )
        }
        actions={
          <>
            <DateField
              id="order-readiness-date"
              size="small"
              label={label('ORDER_REPORTING.DATE')}
              value={date || data?.date || ''}
              onChange={(value) => !isInvalidDate(value) && setDate(value)}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeInternal}
                  onChange={(event) => setIncludeInternal(event.target.checked)}
                  color="primary"
                />
              }
              label={label('ORDER_REPORTING.INCLUDE_INTERNAL')}
            />
            <Button
              variant="outlined"
              disabled={!rows.length}
              onClick={exportCsv}
            >
              {intl.formatMessage(
                { id: 'ORDER_REPORTING.EXPORT_CSV' },
                { count: rows.length },
              )}
            </Button>
          </>
        }
      />

      {loading && (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress
            aria-label={label('ORDER_REPORTING.READINESS_LOADING')}
          />
        </Box>
      )}
      {!loading && (status === 401 || status === 403) && (
        <Alert severity="error">{label('ORDER_REPORTING.UNAUTHORIZED')}</Alert>
      )}
      {!loading && !data && status !== 401 && status !== 403 && (
        <Alert severity="error">
          {label('ORDER_REPORTING.READINESS_ERROR')}
        </Alert>
      )}
      {!loading && data && (
        <>
          <ReadinessSummary
            data={data}
            status={filters.status}
            onStatusChange={(value) => updateFilters({ status: value })}
          />

          <Styles.FilterBar>
            <TextField
              id="order-readiness-search"
              size="small"
              variant="outlined"
              label={label('ORDER_REPORTING.FILTER')}
              value={filters.query}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateFilters({ query: event.target.value })
              }
            />
            <Styles.FilterField>
              <SearchableSelect
                id="order-readiness-client"
                label={label('ORDERS.CLIENT')}
                options={clientOptions}
                value={filters.clientId}
                onChange={(value) => updateFilters({ clientId: value })}
                fullWidth
              />
            </Styles.FilterField>
            <Styles.FilterField>
              <SearchableSelect
                id="order-readiness-owner"
                label={label('ORDERS.OWNER')}
                options={ownerOptions}
                value={filters.owner}
                onChange={(value) => updateFilters({ owner: value })}
                fullWidth
              />
            </Styles.FilterField>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={filters.reportingOnly}
                  onChange={(event) =>
                    updateFilters({ reportingOnly: event.target.checked })
                  }
                  color="primary"
                />
              }
              label={label('ORDER_REPORTING.REPORTING_ONLY')}
            />
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage(
                { id: 'ORDER_REPORTING.SHOWN' },
                { shown: rows.length, total: problems.length },
              )}
            </Typography>
            {canManage && issueGroups.length > 0 && (
              <Box display="flex" flexWrap="wrap" gridGap={8} ml="auto">
                {issueGroups.map((group) => (
                  <Button
                    key={group.clientId}
                    size="small"
                    color="primary"
                    variant="contained"
                    onClick={() => history.push(bulkIssueUrl(group))}
                  >
                    {issueGroups.length === 1
                      ? intl.formatMessage(
                          { id: 'ORDER_REPORTING.ISSUE_ORDERS' },
                          { count: group.consultantIds.length },
                        )
                      : intl.formatMessage(
                          { id: 'ORDER_REPORTING.ISSUE_ORDERS_FOR_CLIENT' },
                          {
                            client: group.clientName,
                            count: group.consultantIds.length,
                          },
                        )}
                  </Button>
                ))}
              </Box>
            )}
          </Styles.FilterBar>

          {problems.length === 0 && (
            <Alert severity="success">
              {label('ORDER_REPORTING.NO_PROBLEMS')}
            </Alert>
          )}
          {problems.length > 0 && rows.length === 0 && (
            <Alert severity="info">
              {label('ORDER_REPORTING.FILTER_EMPTY')}
            </Alert>
          )}
          {rows.length > 0 && (
            <Paper variant="outlined">
              <TableContainer style={{ maxHeight: '70vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {canManage && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={
                              issuableKeys.length > 0 &&
                              pickedCount === issuableKeys.length
                            }
                            indeterminate={
                              pickedCount > 0 &&
                              pickedCount < issuableKeys.length
                            }
                            disabled={!issuableKeys.length}
                            onChange={(event) =>
                              toggle(issuableKeys, event.target.checked)
                            }
                            inputProps={{
                              'aria-label': label('ORDER_REPORTING.SELECT_ALL'),
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell>{label('ORDERS.CONSULTANT')}</TableCell>
                      <TableCell>{label('ORDERS.CLIENT')}</TableCell>
                      <TableCell>{label('ORDERS.STATUS')}</TableCell>
                      <TableCell>{label('ORDERS.NUMBER')}</TableCell>
                      <TableCell>{label('ORDERS.DEADLINE')}</TableCell>
                      <TableCell>{label('ORDERS.OWNER')}</TableCell>
                      <TableCell align="right">
                        {label('ORDER_REPORTING.COLUMN_HOURS')}
                      </TableCell>
                      <TableCell align="right">
                        {label('ORDER_REPORTING.COLUMN_WITHOUT_ORDER')}
                      </TableCell>
                      <TableCell align="right">
                        {label('ORDER_REPORTING.COLUMN_CORRECTIONS')}
                      </TableCell>
                      <TableCell>
                        {label('ORDER_REPORTING.COLUMN_LAST_REPORTED')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{pageRows.map(renderRow)}</TableBody>
                </Table>
              </TableContainer>
              {rows.length > PAGE_SIZE && (
                <TablePagination
                  component="div"
                  count={rows.length}
                  page={page}
                  rowsPerPage={PAGE_SIZE}
                  rowsPerPageOptions={[]}
                  onPageChange={(_event, next) => setPage(next)}
                  labelDisplayedRows={({ from, to, count }) =>
                    displayLabelRows({ from, to, count, intl })
                  }
                />
              )}
            </Paper>
          )}
        </>
      )}
    </OrderStyles.Page>
  );
};

const OrderReportingCompleteness = () => (
  <OrderAccess>
    {({ canManage }) => (
      <OrderReportingCompletenessContent canManage={canManage} />
    )}
  </OrderAccess>
);

export default OrderReportingCompleteness;
